import { X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { usePdfSignedUrl, withPdfViewerParams, type PdfSource } from '../lib/usePdfSignedUrl';
import { PdfFrame } from './PdfPreviewModal';

/**
 * Side-by-side PDF compare — two documents on screen at once.
 *
 * Built for exactly one decision: the near-duplicate warning asks "is this the
 * same day's work as that other report?", and answering it by toggling one
 * viewer meant scroll down, click, read, scroll up, click, read, and hold the
 * first one in your head. Both panes visible turns that into a glance.
 *
 * Each pane fetches its own signed URL and owns its own failure, so one
 * unreachable document still leaves the other readable — the comparison is
 * degraded, not lost. Panes scroll independently: the two reports rarely have
 * the same page count, so locking their scroll together would fight the user.
 *
 * Below ~1100px the panes stack vertically instead of narrowing. Two
 * half-width PDF viewers on a laptop are two documents you can't read, which
 * is worse than one scroll.
 *
 * SECOND CALLER (2026-08-02): the PCO Sources tab compares a daily's ORIGINAL
 * against the regenerated copy that was actually sent. Nothing about the modal
 * changed for it — the pane spec simply widened from a daily-PDF id to any
 * `PdfSource`, which is what the pane's own hook already accepted. The two
 * documents there share a filename by design (a send attaches the regenerated
 * copy under the original's name), so the badges are what tell them apart.
 */
export interface ComparePaneSpec {
  /** Any document the API can presign — a daily-PDF id, or a tagged source. */
  pdfId: PdfSource;
  /** Filename as the PM knows it — shown verbatim in the pane header. */
  filename: string;
  /** Short qualifier beside the filename: "this PDF", "saved", "pending". */
  badge: string;
  /** Red badge for the flagged sibling; neutral for the one in hand. */
  badgeTone?: 'neutral' | 'danger';
}

export function PdfCompareModal({
  left,
  right,
  onClose,
}: {
  left: ComparePaneSpec;
  right: ComparePaneSpec;
  onClose: () => void;
}) {
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portaled to <body>. `main` is `position: relative; z-index: 1`, which makes
  // it a STACKING CONTEXT — so an overlay rendered inside it is clamped below
  // the rail no matter how high its own z-index goes. The overlay was already
  // full-viewport and centered; the rail was simply painting over its left edge,
  // which read as "offset left with the title clipped". The single viewer hid
  // the same bug only because 1100px centers clear of the rail while 1800px
  // does not.
  return createPortal(
    <div
      className="pdf-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Compare PDFs"
    >
      <div className="pdf-modal pdf-compare" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Compare PDFs</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            aria-label="Close PDF comparison"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="pdf-compare-body">
          <ComparePane spec={left} />
          <ComparePane spec={right} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ComparePane({ spec }: { spec: ComparePaneSpec }) {
  // One hook instance per pane — that's what makes the two loads and the two
  // error states genuinely independent.
  const signed = usePdfSignedUrl(spec.pdfId);
  return (
    <div className="pdf-compare-pane">
      <div className="pdf-compare-pane-head">
        <span className="pdf-compare-pane-name mono" title={spec.filename}>
          {spec.filename}
        </span>
        <span
          className={
            spec.badgeTone === 'danger'
              ? 'pdf-compare-badge pdf-compare-badge--danger'
              : 'pdf-compare-badge'
          }
        >
          {spec.badge}
        </span>
      </div>
      <div className="pdf-compare-pane-body">
        {/* Thumbnails collapsed + fit-to-width: a half-width pane has no room
            for Chrome's sidebar. Compare panes only — the single viewer is wide
            enough that the sidebar is useful there. */}
        <PdfFrame {...signed} url={withPdfViewerParams(signed.url)} />
      </div>
    </div>
  );
}
